import {useMemo} from 'react';
import {
  ChartData,
  Asset,
  Position,
  ChartDataItem,
} from '@/providers/overview/OverviewProvider';
import {GROWTH_RANGE} from '@/providers/overview/constants';
import {TOKEN_ADDRESS} from '@/util/chains';

export const useAssets = (chartData: ChartData, positions: Position[]) => {
  return useMemo((): Asset[] => {
    const assets: Record<string, Asset> = {};
    const keiChartDataItem: ChartDataItem | undefined =
      chartData['kei-finance'];

    if (!keiChartDataItem || !positions.length) {
      return [];
    }

    const price = keiChartDataItem?.getPrice(undefined, 0) ?? 0;
    const prevPrice =
      keiChartDataItem?.getPrice(
        keiChartDataItem?.getTimestampFromLast(GROWTH_RANGE),
        0
      ) ?? 0;
    const growth =
      price !== undefined && prevPrice !== undefined
        ? ((price - prevPrice) * 100) / prevPrice
        : 0;

    const keiPosition = positions.find(
      position => position.symbol.toLowerCase() === 'kei'
    )!;
    assets[TOKEN_ADDRESS] = {
      decimals: 18,
      id: TOKEN_ADDRESS,
      name: 'KEI finance',
      image: keiPosition.image,
      color: keiPosition.color,
      symbol: 'KEI',
      balance: '0',
      value: 0,
      price,
      growth,
      chartData: JSON.parse(JSON.stringify(keiChartDataItem.timeSeries)),
    };

    positions.forEach(position => {
      position.assets.forEach(asset => {
        if (!assets[asset.id]) {
          const chartDataItem = chartData[asset.id.toLowerCase()];
          const price = chartDataItem?.getPrice(undefined, 0) ?? 0;
          const prevPrice =
            chartDataItem?.getPrice(
              chartDataItem?.getTimestampFromLast(GROWTH_RANGE),
              0
            ) ?? 0;
          const growth =
            price !== undefined && prevPrice !== undefined
              ? ((price - prevPrice) * 100) / prevPrice
              : 0;

          assets[asset.id] = {
            decimals: 18,
            id: asset.id,
            name: asset.name,
            symbol: asset.symbol,
            image: asset.image ?? position.image,
            balance: '0',
            value: 0,
            color: asset.color,
            price,
            growth,
            chartData: JSON.parse(
              JSON.stringify(
                chartData[asset.id.toLowerCase()]?.timeSeries ?? []
              )
            ),
          };
        }

        const currentAsset = assets[asset.id];
        currentAsset.balance = (
          BigInt(currentAsset.balance) + BigInt(asset.balance)
        ).toString();
        currentAsset.value += asset.value;
      });
    });

    return Object.values(assets).filter(
      asset => asset.balance !== '0' || asset.symbol === 'KEI'
    );
  }, [chartData, positions]);
};
